import {
  Badge,
  Box,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Table,
  HStack,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverHeader,
  PopoverBody,
  VStack,
  IconButton,
  Stack,
} from "@chakra-ui/react";
import {
  FaInstagram,
  FaTiktok,
  FaMailBulk,
  FaSnapchatGhost,
  FaInfoCircle,
} from "react-icons/fa";
import {
  KYDAdminCampaign,
  EventDetailCampaignsSectionData,
} from "@common/interfaces/KYDEvent";
import { KYDTicketType } from "@common/interfaces/KYDTicket";
import { KYDMarketingPlatform } from "@common/interfaces/KYDOrganization";

export const CampaignsTable = ({
  data,
  ...props
}: {
  data: EventDetailCampaignsSectionData;
}) => {
  const badgeForStatus = (ticketType: KYDTicketType) => {
    let bg = "gray";
    let name = "N/A";

    if (ticketType.sold_out) {
      bg = "orange";
      name = "Sold Out";
    } else {
      switch (ticketType.status) {
        case "DRAFT":
          bg = "blue";
          name = "Draft";

          break;
        case "PROCESSING":
          bg = "yellow";
          name = "Processing";

          break;
        case "OPEN":
          bg = "green";
          name = "On Sale";

          break;
        case "OFFSALE":
          bg = "red";
          name = "Off Sale";

          break;
        default: {
        }
      }
    }

    return (
      <Box>
        <Badge
          justifyItems={"center"}
          maxW={"100px"}
          size={"xl"}
          colorScheme={bg}
        >
          {name}
        </Badge>
        {ticketType.status === "OPEN" && ticketType.display_off_sale_at ? (
          <Text fontSize={"xs"} color={"gray.500"}>
            Off sale {ticketType.display_off_sale_at}
          </Text>
        ) : null}
        {ticketType.status === "OFFSALE" && ticketType.display_on_sale_at ? (
          <Text fontSize={"xs"} color={"gray.500"}>
            On sale {ticketType.display_on_sale_at}
          </Text>
        ) : null}
      </Box>
    );
  };

  const platformIcon = (platform: KYDMarketingPlatform) => {
    if (platform === "email") {
      return <FaMailBulk size={"20px"} />;
    } else if (platform === "meta") {
      return <FaInstagram size={"20px"} />;
    } else if (platform === "snapchat") {
      return <FaSnapchatGhost size={"20px"} />;
    } else if (platform === "tiktok") {
      return <FaTiktok size={"20px"} />;
    }
  };

  return (
    <Table {...props}>
      <Thead>
        <Tr>
          <Th></Th>
          <Th>Name</Th>
          <Th>Impressions</Th>
          <Th>Spend</Th>
          <Th>Sales</Th>
          <Th>ROAS</Th>
          <Th>CTR</Th>
          <Th>Clicks</Th>
          <Th></Th>
        </Tr>
      </Thead>
      <Tbody>
        {data.campaigns &&
          data.campaigns.map((campaign: KYDAdminCampaign) => (
            <Tr key={campaign.campaign_id}>
              <Td>{platformIcon(campaign.platform)}</Td>
              <Td>
                <Text>{campaign.campaign_name}</Text>
              </Td>
              <Td>
                <Text>{campaign.display_impressions}</Text>
              </Td>
              <Td>
                <Text>{campaign.display_spend}</Text>
              </Td>
              <Td>
                <HStack spacing={0}>
                  <Text>{campaign.display_roas_gmv}</Text>
                  {campaign.display_events &&
                    campaign.display_events.length > 0 && (
                      <Popover trigger="hover" placement="top">
                        <PopoverTrigger>
                          <Box as="span" ml={1} cursor="pointer">
                            <IconButton
                              icon={<FaInfoCircle />}
                              variant="ghost"
                              aria-label="View Details"
                            />
                          </Box>
                        </PopoverTrigger>
                        <PopoverContent p={3} width="auto" minWidth="250px">
                          <PopoverArrow />
                          <PopoverHeader fontWeight="bold">
                            <Stack spacing={1}>
                              <Text>By Event</Text>
                              <Text
                                fontWeight={"normal"}
                                fontSize={"sm"}
                                color="gray.500"
                              >
                                This data is pulled directly from Meta. It is an
                                estimation on their end.
                              </Text>
                            </Stack>
                          </PopoverHeader>
                          <PopoverBody>
                            <Box maxHeight="300px" overflowY="auto">
                              <VStack align="stretch" spacing={2}>
                                {campaign.display_events.map((event, index) => (
                                  <Box key={index}>
                                    <a
                                      href={event.link}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      <Text fontWeight="medium">
                                        {event.name}
                                      </Text>
                                    </a>
                                    <HStack>
                                      <Text fontSize="sm">Sales:</Text>
                                      <Text fontSize="sm">
                                        {event.display_ticket_gmv}
                                      </Text>
                                    </HStack>
                                    <HStack>
                                      <Text fontSize="sm">ROAS:</Text>
                                      <Text fontSize="sm">
                                        {event.display_roas}
                                      </Text>
                                    </HStack>
                                  </Box>
                                ))}
                              </VStack>
                            </Box>
                          </PopoverBody>
                        </PopoverContent>
                      </Popover>
                    )}
                </HStack>
              </Td>
              <Td>
                <Text>{campaign.display_kyd_roas}</Text>
              </Td>
              <Td>
                <Text>{campaign.display_ctr}</Text>
              </Td>
              <Td>
                <Text>{campaign.display_clicks}</Text>
              </Td>
            </Tr>
          ))}
      </Tbody>
    </Table>
  );
};
